import { blankSheetProps } from "./sheets";
import { z } from "zod";
import type { TLRecord, TLShape } from "@tldraw/tlschema";
import type { OperationContext } from "./agent-operation-context";
import { richTextPlain } from "./agent-text-edit";
export type Bounds = { x: number; y: number; w: number; h: number };
export const measuredBounds = z
  .object({
    revision: z.number().int().min(0),
    bounds: z
      .array(
        z
          .object({
            id: z.string(),
            x: z.number().finite(),
            y: z.number().finite(),
            w: z.number().min(0).max(100000),
            h: z.number().min(0).max(100000),
          })
          .strict(),
      )
      .max(2000),
  })
  .strict();
export type Measurements = z.infer<typeof measuredBounds>;
const id = z.string().regex(/^shape:[\w-]{1,100}$/);
export const layoutOperations = [
  z
    .object({
      type: z.literal("arrange"),
      id: z.string().regex(/^(shape|page):[\w-]+$/),
      ids: z.array(id).min(1).max(100),
      mode: z.enum([
        "grid",
        "stack",
        "row",
        "align_left",
        "align_right",
        "align_top",
        "align_bottom",
        "align_center",
        "distribute_horizontal",
        "distribute_vertical",
      ]),
      x: z.number().finite().optional(),
      y: z.number().finite().optional(),
      columns: z.number().int().min(1).max(50).optional(),
      gap: z.number().min(0).max(1000).default(24),
      measured: measuredBounds.optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("place_relative"),
      id,
      referenceId: id,
      side: z.enum(["below", "right"]),
      gap: z.number().min(0).max(1000).default(24),
      measured: measuredBounds.optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("fit_sheet"),
      id,
      margin: z.number().min(0).max(1000).default(24),
      measured: measuredBounds.optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("resize_sheet"),
      id,
      w: z.number().positive().max(10000).optional(),
      h: z.number().positive().max(10000).optional(),
      preset: z
        .enum(["A4", "A3", "Letter", "16:9", "4:3", "1:1", "Custom"])
        .optional(),
      landscape: z.boolean().default(true),
      widthMm: z.number().min(50).max(2000).optional(),
      heightMm: z.number().min(50).max(2000).optional(),
      units: z.enum(["canvas", "mm"]).default("canvas"),
    })
    .strict(),
] as const;
const operation = z.discriminatedUnion("type", layoutOperations);
export function unionBounds(boxes: Bounds[]): Bounds {
  const x = Math.min(...boxes.map((b) => b.x)),
    y = Math.min(...boxes.map((b) => b.y));
  return {
    x,
    y,
    w: Math.max(...boxes.map((b) => b.x + b.w)) - x,
    h: Math.max(...boxes.map((b) => b.y + b.h)) - y,
  };
}
export function intrinsicBounds(
  store: Record<string, TLRecord>,
  shape: TLShape,
  measured?: Measurements,
  depth = 0,
): Bounds {
  const exact = measured?.bounds.find((b) => b.id === shape.id);
  if (exact) return exact;
  if (depth > 32) throw new Error("Nested group bounds exceed the depth limit");
  if (shape.type === "group") {
    const children = Object.values(store).filter(
      (r): r is TLShape => r.typeName === "shape" && r.parentId === shape.id,
    );
    return children.length
      ? unionBounds(
          children.map((s) => shapeBounds(store, s, measured, depth + 1)),
        )
      : { x: 0, y: 0, w: 0, h: 0 };
  }
  const props = shape.props as Record<string, unknown>;
  let w = Number(props.w) || 0,
    h = Number(props.h) || 0;
  if (shape.type === "text") {
    const size =
      Number(shape.meta.calqueTextSize) ||
      ({ s: 18, m: 24, l: 36, xl: 48 }[shape.props.size] ?? 24);
    const plain = richTextPlain(shape.props.richText);
    w = w || Math.max(40, plain.length * size);
    const chars = Math.max(1, Math.floor(Math.max(1, w - 32) / (size * 1.1)));
    h =
      plain
        .split("\n")
        .reduce(
          (n, line) => n + Math.max(1, Math.ceil(line.length / chars)),
          0,
        ) *
        size *
        1.5 +
      32;
  }
  if (shape.type === "geo") h += shape.props.growY;
  if (!w || !h) {
    const points = props.points;
    if (Array.isArray(points) && points.length) {
      const xs = points.map((p) => Number(p.x)),
        ys = points.map((p) => Number(p.y));
      return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        w: Math.max(...xs) - Math.min(...xs),
        h: Math.max(...ys) - Math.min(...ys),
      };
    }
  }
  return { x: 0, y: 0, w: w || 100, h: h || 100 };
}
export function shapeBounds(
  store: Record<string, TLRecord>,
  shape: TLShape,
  measured?: Measurements,
  depth = 0,
): Bounds {
  const b = intrinsicBounds(store, shape, measured, depth),
    cos = Math.cos(shape.rotation),
    sin = Math.sin(shape.rotation);
  const points = [
    [b.x, b.y],
    [b.x + b.w, b.y],
    [b.x, b.y + b.h],
    [b.x + b.w, b.y + b.h],
  ].map(([x, y]) => ({
    x: shape.x + x * cos - y * sin,
    y: shape.y + x * sin + y * cos,
  }));
  const x = Math.min(...points.map((p) => p.x)),
    y = Math.min(...points.map((p) => p.y));
  return {
    x,
    y,
    w: Math.max(...points.map((p) => p.x)) - x,
    h: Math.max(...points.map((p) => p.y)) - y,
  };
}
export function planArrangement(
  store: Record<string, TLRecord>,
  shapes: TLShape[],
  options: {
    mode: string;
    x?: number;
    y?: number;
    gap: number;
    columns?: number;
    measured?: Measurements;
  },
) {
  if (new Set(shapes.map((s) => s.parentId)).size !== 1)
    throw new Error("Arrange objects sharing one parent");
  const boxes = shapes.map((s) => shapeBounds(store, s, options.measured)),
    all = unionBounds(boxes),
    updates: { id: string; x: number; y: number }[] = [];
  const cols = options.columns ?? Math.ceil(Math.sqrt(shapes.length)),
    cellW = Math.max(...boxes.map((b) => b.w)),
    cellH = Math.max(...boxes.map((b) => b.h));
  const order = shapes
    .map((_, i) => i)
    .sort((a, b) =>
      options.mode.endsWith("vertical")
        ? boxes[a].y - boxes[b].y
        : boxes[a].x - boxes[b].x,
    );
  let cursor = 0;
  const horizontal = options.mode === "distribute_horizontal",
    vertical = options.mode === "distribute_vertical";
  const spacing =
    (horizontal || vertical) && shapes.length > 1
      ? ((horizontal ? all.w : all.h) -
          boxes.reduce((sum, b) => sum + (horizontal ? b.w : b.h), 0)) /
        (shapes.length - 1)
      : options.gap;
  if (spacing < 0)
    throw new Error(
      "There is not enough space to distribute these objects without overlap",
    );
  for (const [rank, index] of (horizontal || vertical
    ? order
    : shapes.map((_, i) => i)
  ).entries()) {
    const shape = shapes[index],
      b = boxes[index];
    let x = b.x,
      y = b.y;
    switch (options.mode) {
      case "grid":
        x = (options.x ?? all.x) + (rank % cols) * (cellW + options.gap);
        y =
          (options.y ?? all.y) +
          Math.floor(rank / cols) * (cellH + options.gap);
        break;
      case "stack":
        x = options.x ?? all.x;
        y = (options.y ?? all.y) + cursor;
        cursor += b.h + options.gap;
        break;
      case "row":
        x = (options.x ?? all.x) + cursor;
        y = options.y ?? all.y;
        cursor += b.w + options.gap;
        break;
      case "align_left":
        x = all.x;
        break;
      case "align_right":
        x = all.x + all.w - b.w;
        break;
      case "align_top":
        y = all.y;
        break;
      case "align_bottom":
        y = all.y + all.h - b.h;
        break;
      case "align_center":
        x = all.x + (all.w - b.w) / 2;
        break;
      case "distribute_horizontal":
        x = all.x + cursor;
        cursor += b.w + spacing;
        break;
      case "distribute_vertical":
        y = all.y + cursor;
        cursor += b.h + spacing;
        break;
    }
    updates.push({ id: shape.id, x: shape.x + x - b.x, y: shape.y + y - b.y });
  }
  return updates;
}
export function layoutDiagnostics(
  store: Record<string, TLRecord>,
  parentId: string,
  measured?: Measurements,
  ignoreIds: string[] = [],
) {
  const children = Object.values(store).filter(
    (r): r is TLShape =>
      r.typeName === "shape" &&
      r.parentId === parentId &&
      !ignoreIds.includes(r.id),
  );
  const bounds = children.map((s) => ({
      id: s.id,
      ...shapeBounds(store, s, measured),
    })),
    overlaps: string[][] = [];
  for (let i = 0; i < bounds.length && overlaps.length < 200; i++)
    for (let j = i + 1; j < bounds.length; j++) {
      const a = bounds[i],
        b = bounds[j];
      if (
        a.x < b.x + b.w - 0.5 &&
        a.x + a.w > b.x + 0.5 &&
        a.y < b.y + b.h - 0.5 &&
        a.y + a.h > b.y + 0.5
      )
        overlaps.push([a.id, b.id]);
      if (overlaps.length >= 200) break;
    }
  const parent = store[parentId],
    w =
      parent?.typeName === "shape" && "w" in parent.props
        ? Number(parent.props.w)
        : Infinity,
    h =
      parent?.typeName === "shape" && "h" in parent.props
        ? Number(parent.props.h)
        : Infinity;
  return {
    measurement: measured ? "rendered" : "estimated",
    bounds,
    overlaps,
    overflow: bounds
      .filter((b) => b.x < 0 || b.y < 0 || b.x + b.w > w || b.y + b.h > h)
      .map((b) => b.id),
    hint: measured
      ? undefined
      : "Use layout returned by render_sheet/render_status for exact font and group geometry; pass it as measured with the same revision.",
  };
}
export function applyLayoutOperation(input: unknown, c: OperationContext) {
  const parsed = operation.safeParse(input);
  if (!parsed.success) return false;
  const op = parsed.data;
  const measured = "measured" in op ? op.measured : undefined;
  if (measured && measured.revision !== c.revision)
    throw new Error(
      "Rendered measurements are stale; read/render the current revision",
    );
  const store = Object.fromEntries(c.txn.entries());
  if (op.type === "arrange") {
    const shapes = op.ids.map((id) => c.editable(id));
    if (shapes.some((s) => s.parentId !== op.id))
      throw new Error("arrange.id must be the shared parent");
    if (shapes.some((s) => s.type === "sheet"))
      throw new Error("Arrange sheet contents, not sheets");
    for (const update of planArrangement(store, shapes, op)) {
      const shape = c.editable(update.id);
      c.set({ ...shape, x: update.x, y: update.y });
    }
    return true;
  }
  const shape = c.editable(op.id);
  if (op.type === "place_relative") {
    const ref = c.editable(op.referenceId);
    if (ref.parentId !== shape.parentId || ref.id === shape.id)
      throw new Error(
        "Reference must be a different object with the same parent",
      );
    const a = shapeBounds(store, shape, measured),
      b = shapeBounds(store, ref, measured);
    c.set({
      ...shape,
      x: shape.x + (op.side === "right" ? b.x + b.w + op.gap : b.x) - a.x,
      y: shape.y + (op.side === "below" ? b.y + b.h + op.gap : b.y) - a.y,
    });
    return true;
  }
  if (shape.type !== "sheet") throw new Error("Choose a sheet");
  if (shape.props.preview || shape.props.source)
    throw new Error(
      "Resize source drawings in the app with calibration/alignment review; fit-to-content supports blank composition sheets",
    );
  if (op.type === "resize_sheet") {
    if (op.preset && (op.w !== undefined || op.h !== undefined))
      throw new Error("Choose a paper preset or explicit dimensions");
    if (!op.preset && (!op.w || !op.h))
      throw new Error("Supply both width and height");
    if (op.preset === "Custom" && (!op.widthMm || !op.heightMm))
      throw new Error("Custom sheets need widthMm and heightMm");
    const paper =
      op.preset && op.preset !== "Custom"
        ? blankSheetProps("", op.preset, op.landscape)
        : undefined;
    const w =
        paper?.w ??
        (op.preset
          ? (op.widthMm! * 8) / 3
          : op.w! * (op.units === "mm" ? 8 / 3 : 1)),
      h =
        paper?.h ??
        (op.preset
          ? (op.heightMm! * 8) / 3
          : op.h! * (op.units === "mm" ? 8 / 3 : 1));
    if (w > 10000 || h > 10000) throw new Error("Sheet is too large");
    c.set({
      ...shape,
      props: { ...shape.props, w, h, paper: op.preset ?? "Custom" },
      meta: {
        ...shape.meta,
        paperWidthMm: (w * 3) / 8,
        paperHeightMm: (h * 3) / 8,
      },
    });
    return true;
  }
  const children = Object.values(store).filter(
    (r): r is TLShape => r.typeName === "shape" && r.parentId === shape.id,
  );
  if (!children.length) throw new Error("Sheet has no contents to fit");
  const b = unionBounds(
      children.map((child) => shapeBounds(store, child, measured)),
    ),
    dx = b.x - op.margin,
    dy = b.y - op.margin,
    w = b.w + 2 * op.margin,
    h = b.h + 2 * op.margin;
  if (w > 10000 || h > 10000 || w <= 0 || h <= 0)
    throw new Error("Fitted sheet size is out of range");
  for (const child of children) c.editable(child.id);
  for (const child of children)
    c.set({ ...child, x: child.x - dx, y: child.y - dy });
  c.set({
    ...shape,
    x: shape.x + dx * Math.cos(shape.rotation) - dy * Math.sin(shape.rotation),
    y: shape.y + dx * Math.sin(shape.rotation) + dy * Math.cos(shape.rotation),
    props: { ...shape.props, w, h, paper: "Custom" },
    meta: {
      ...shape.meta,
      paperWidthMm: (w * 3) / 8,
      paperHeightMm: (h * 3) / 8,
    },
  });
  return true;
}
